import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  Filters,
  ChoiceList,
  TextField,
  InlineStack,
  Text,
  Pagination,
  BlockStack,
  SkeletonBodyText,
  EmptyState,
  Box,
  Modal,
} from '@shopify/polaris';
import { salesAgentsApi } from '@/services';
import type { SalesAgentWithStats } from '@/services/salesAgents';

export default function SalesAgentsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAgent, setNewAgent] = useState({
    name: '',
    email: '',
    phone: '',
    commissionRate: 0.10,
  });
  const limit = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['salesAgents', page, status],
    queryFn: () =>
      salesAgentsApi.list({
        page,
        limit,
        status: status[0],
      }),
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof newAgent) => salesAgentsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salesAgents'] });
      setShowCreateModal(false);
      setNewAgent({ name: '', email: '', phone: '', commissionRate: 0.10 });
    },
  });

  const handleStatusChange = useCallback((value: string[]) => {
    setStatus(value);
    setPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatus([]);
  }, []);

  const handleCreateAgent = useCallback(() => {
    if (!newAgent.name || !newAgent.email) return;
    createMutation.mutate(newAgent);
  }, [newAgent, createMutation]);

  const getStatusBadge = (agentStatus: string) => {
    switch (agentStatus) {
      case 'active':
        return <Badge tone="success">Active</Badge>;
      case 'inactive':
        return <Badge>Inactive</Badge>;
      default:
        return <Badge>{agentStatus}</Badge>;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const formatPercent = (rate: number) => {
    return `${(Number(rate) * 100).toFixed(1)}%`;
  };

  const rows = (data?.data || []).map((agent: SalesAgentWithStats) => [
    <Button key={agent.id} variant="plain" onClick={() => navigate(`/sales/${agent.id}`)}>
      {agent.name}
    </Button>,
    agent.email,
    agent.phone || 'N/A',
    formatPercent(agent.commissionRate),
    agent.clinicCount || 0,
    formatCurrency(agent.pendingCommissions || 0),
    getStatusBadge(agent.status),
    <Button key={`view-${agent.id}`} onClick={() => navigate(`/sales/${agent.id}`)}>
      View
    </Button>,
  ]);

  const filters = [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          choices={[
            { label: 'Active', value: 'active' },
            { label: 'Inactive', value: 'inactive' },
          ]}
          selected={status}
          onChange={handleStatusChange}
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = status.length > 0
    ? [{ key: 'status', label: `Status: ${status.join(', ')}`, onRemove: () => setStatus([]) }]
    : [];

  if (error) {
    return (
      <Page title="Sales Agents">
        <Card>
          <EmptyState
            heading="Error loading sales agents"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Please try again later.</p>
          </EmptyState>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      title="Sales Agents"
      primaryAction={{
        content: 'Add Agent',
        onAction: () => setShowCreateModal(true),
      }}
    >
      <Card padding="0">
        <Box padding="400">
          <Filters
            queryValue=""
            queryPlaceholder="Search agents..."
            filters={filters}
            appliedFilters={appliedFilters}
            onQueryChange={() => {}}
            onQueryClear={() => {}}
            onClearAll={handleClearFilters}
          />
        </Box>

        {isLoading ? (
          <Box padding="400">
            <SkeletonBodyText lines={10} />
          </Box>
        ) : rows.length === 0 ? (
          <Box padding="800">
            <EmptyState
              heading="No sales agents found"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              action={{ content: 'Add Agent', onAction: () => setShowCreateModal(true) }}
            >
              <p>Add your first sales agent to get started.</p>
            </EmptyState>
          </Box>
        ) : (
          <>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'numeric', 'text', 'text']}
              headings={['Name', 'Email', 'Phone', 'Commission', 'Clinics', 'Pending', 'Status', 'Action']}
              rows={rows}
            />
            {data && data.meta.totalPages > 1 && (
              <Box padding="400">
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    hasNext={page < data.meta.totalPages}
                    onPrevious={() => setPage((p) => p - 1)}
                    onNext={() => setPage((p) => p + 1)}
                  />
                </InlineStack>
              </Box>
            )}
          </>
        )}
      </Card>

      {data && (
        <Box paddingBlockStart="400">
          <Text as="p" tone="subdued" alignment="center">
            Showing {data.data.length} of {data.meta.total} sales agents
          </Text>
        </Box>
      )}

      {/* Create Agent Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add New Sales Agent"
        primaryAction={{
          content: 'Add Agent',
          onAction: handleCreateAgent,
          disabled: !newAgent.name || !newAgent.email,
          loading: createMutation.isPending,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setShowCreateModal(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Name"
              value={newAgent.name}
              onChange={(value) => setNewAgent((prev) => ({ ...prev, name: value }))}
              autoComplete="off"
              requiredIndicator
            />
            <TextField
              label="Email"
              type="email"
              value={newAgent.email}
              onChange={(value) => setNewAgent((prev) => ({ ...prev, email: value }))}
              autoComplete="off"
              requiredIndicator
            />
            <TextField
              label="Phone"
              type="tel"
              value={newAgent.phone}
              onChange={(value) => setNewAgent((prev) => ({ ...prev, phone: value }))}
              autoComplete="off"
            />
            <TextField
              label="Commission Rate (%)"
              type="number"
              value={String(newAgent.commissionRate * 100)}
              onChange={(value) => setNewAgent((prev) => ({ ...prev, commissionRate: parseFloat(value) / 100 || 0 }))}
              autoComplete="off"
              suffix="%"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
